/**
 * PrivateLayout — Shared sidebar layout for B2C module pages
 * Provides navigation sidebar consistent with ChatPage
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ModuleFlyout, ModuleTriggerButton } from '../../components/ModuleFlyout';
import { PrivatFlyout, PrivatTriggerButton } from '../../components/PrivatFlyout';
import PrivateAIChatWidget from '../../components/common/PrivateAIChatWidget';
import {
  Menu, X, FileText, Clock, ShoppingBag, User, LogOut, MessageSquare, Zap
} from 'lucide-react';

export default function PrivateLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moduleFlyoutOpen, setModuleFlyoutOpen] = useState(false);
  const [privatFlyoutOpen, setPrivatFlyoutOpen] = useState(false);

  const creditsUsed = user?.credits_used || 0;
  const creditsTotal = user?.credits_total || 70;
  const creditsRemaining = Math.max(0, creditsTotal - creditsUsed);
  const usagePercent = Math.min(100, Math.round((creditsUsed / creditsTotal) * 100));
  const tier = user?.tier || 'free';
  const tierLabel = tier === 'enterprise' ? 'Enterprise' : tier === 'premium' ? 'Premium' : 'Gratis';

  const navItems = [
    { path: '/chat', icon: MessageSquare, label: 'Chat', testId: 'nav-chat' },
    { path: '/documents', icon: FileText, label: 'Dokumenter', testId: 'nav-documents' },
    { path: '/timeline', icon: Clock, label: 'Tidslinje', testId: 'nav-timeline' },
  ];

  return (
    <div className="h-screen flex bg-[#0B1120] noise-overlay" data-testid="private-layout">
      {/* Flyouts at root level */}
      <ModuleFlyout isOpen={moduleFlyoutOpen} onClose={() => setModuleFlyoutOpen(false)} />
      <PrivatFlyout isOpen={privatFlyoutOpen} onClose={() => setPrivatFlyoutOpen(false)} />

      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-3 left-3 z-50 p-2 bg-[#131C2E] border border-white/10 rounded-lg md:hidden"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-[#F0F4F8]" /> : <Menu className="w-5 h-5 text-[#F0F4F8]" />}
      </button>

      {/* Sidebar */}
      <div
        className={`${sidebarOpen ? 'w-72 mobile-open' : 'w-0 md:w-56'} transition-all duration-300 bg-[#131C2E]/95 backdrop-blur-md border-r border-white/5 flex flex-col overflow-hidden flex-shrink-0`}
        data-testid="private-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RettVerk" className="w-8 h-8 rounded object-contain flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-sm font-bold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>RettVerk 1.2.0</h1>
              <p className="text-[10px] text-[#8899A8] tracking-wider uppercase">Juridisk assistent</p>
            </div>
          </div>
        </div>

        {/* Credits/Usage */}
        <div className="px-3 py-2 border-b border-white/5">
          <div className="bg-[#1B2740]/60 rounded-lg p-2.5" data-testid="credits-widget">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#C07850]" />
                <span className="text-[11px] font-medium text-[#F0F4F8]">{tierLabel}</span>
              </div>
              <span className="text-[10px] text-[#8899A8]" data-testid="credits-remaining">{creditsRemaining} igjen</span>
            </div>
            <div className="w-full h-1.5 bg-[#0B1120] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usagePercent}%`,
                  backgroundColor: usagePercent > 80 ? '#ef4444' : usagePercent > 50 ? '#eab308' : '#C07850'
                }}
                data-testid="credits-bar"
              />
            </div>
            <p className="text-[9px] text-[#8899A8] mt-1">{creditsUsed} / {creditsTotal} brukt</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <a
                key={item.path}
                href={item.path}
                data-testid={item.testId}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-[#1B2740] text-[#F0F4F8] border border-[#C07850]/20' : 'text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 border border-transparent'}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </a>
            );
          })}
        </div>

        {/* Footer — Moduler, Kjøp, Profil, Logg ut */}
        <div className="p-3 border-t border-white/5 space-y-1">
          <ModuleTriggerButton
            onClick={() => { setModuleFlyoutOpen(!moduleFlyoutOpen); setPrivatFlyoutOpen(false); }}
            isOpen={moduleFlyoutOpen}
          />
          <div className="mt-1">
            <PrivatTriggerButton
              onClick={() => { setPrivatFlyoutOpen(!privatFlyoutOpen); setModuleFlyoutOpen(false); }}
              isOpen={privatFlyoutOpen}
            />
          </div>

          {user?.account_type !== 'business' && (
            <a
              href="/konto/kjop"
              data-testid="nav-purchase"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm mt-1"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Kjøp moduler</span>
            </a>
          )}

          <a
            href="/profile"
            data-testid="nav-profile"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 transition-colors text-sm"
          >
            <User className="w-4 h-4" />
            <span>Min profil</span>
          </a>

          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#8899A8] hover:text-red-400 hover:bg-red-500/5 transition-colors text-sm w-full"
          >
            <LogOut className="w-4 h-4" />
            <span>Logg ut</span>
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto min-w-0">
        {children}
      </div>

      {/* B2C AI Chat Widget */}
      <PrivateAIChatWidget />
    </div>
  );
}
